import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { inventoryAPI } from '../../services';
import { toast } from 'react-toastify';

function getDefaultPackage(material) {
  const pkgs = material?.packages || [];
  return pkgs.find((p) => p.is_default) ?? pkgs[0] ?? null;
}

export default function BulkImportModal({ show, onHide, branchId, materials, suppliers, onSuccess }) {
  const [qty, setQty] = useState({});
  const [price, setPrice] = useState({});
  // pkgSel[materialId] = selected package id
  const [pkgSel, setPkgSel] = useState({});
  const [supplierId, setSupplierId] = useState('');
  const [loading, setLoading] = useState(false);

  const sortedMaterials = useMemo(
    () => [...(materials || [])].filter((m) => m.is_active !== false).sort((a, b) => a.name.localeCompare(b.name, 'vi')),
    [materials],
  );

  useEffect(() => {
    if (show) {
      setQty({});
      setPrice({});
      // Init each material to its default package
      const defaults = {};
      (materials || []).forEach((m) => {
        const pkg = getDefaultPackage(m);
        if (pkg) defaults[m.id] = pkg.id;
      });
      setPkgSel(defaults);
      setSupplierId('');
    }
  }, [show, materials]);

  const filledCount = useMemo(
    () => sortedMaterials.filter((m) => Number(qty[m.id]) > 0).length,
    [sortedMaterials, qty],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = sortedMaterials
      .filter((m) => Number(qty[m.id]) > 0)
      .map((m) => ({
        material_id: m.id,
        quantity: Number(qty[m.id]),
        package_id: pkgSel[m.id] ?? undefined,
        unit_price: price[m.id] ? Number(price[m.id]) : undefined,
      }));

    if (items.length === 0) {
      toast.error('Chưa nhập số lượng cho NVL nào');
      return;
    }

    try {
      setLoading(true);
      await inventoryAPI.bulkImport({
        branch_id: branchId,
        supplier_id: supplierId || undefined,
        items,
      });
      toast.success(`Đã nhập kho ${items.length} NVL`);
      if (onSuccess) onSuccess();
      onHide();
    } catch (err) {
      toast.error(err?.message || 'Không thể nhập kho');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            Nhập kho hàng loạt
            {filledCount > 0 && (
              <span className="fs-6 fw-normal text-primary ms-2">· {filledCount} NVL</span>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3" style={{ maxWidth: 320 }}>
            <Form.Label className="fw-semibold">Nhà cung cấp (áp dụng tất cả)</Form.Label>
            <Form.Select size="sm" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
              <option value="">-- Không có --</option>
              {(suppliers || []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <Table bordered size="sm" className="mb-0" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '35%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '25%' }} />
              </colgroup>
              <thead className="table-light sticky-top">
                <tr>
                  <th>Nguyên vật liệu</th>
                  <th>Số lượng nhập</th>
                  <th>Đơn giá (VNĐ)</th>
                  <th>Đơn vị / Quy cách</th>
                </tr>
              </thead>
              <tbody>
                {sortedMaterials.map((m) => {
                  const pkgs = m.packages || [];
                  const selectedPkgId = pkgSel[m.id] ?? '';
                  const selectedPkg = pkgs.find((p) => p.id === selectedPkgId) ?? pkgs[0];
                  const hasQty = Number(qty[m.id]) > 0;
                  return (
                    <tr key={m.id} className={hasQty ? 'table-primary' : ''}>
                      <td className="align-middle fw-medium">{m.name}</td>
                      <td>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.001"
                          size="sm"
                          value={qty[m.id] ?? ''}
                          placeholder="0"
                          onChange={(e) =>
                            setQty((prev) => ({ ...prev, [m.id]: e.target.value }))
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min="0"
                          size="sm"
                          value={price[m.id] ?? ''}
                          placeholder="—"
                          onChange={(e) =>
                            setPrice((prev) => ({ ...prev, [m.id]: e.target.value }))
                          }
                        />
                      </td>
                      <td>
                        {pkgs.length > 1 ? (
                          <Form.Select
                            size="sm"
                            value={selectedPkgId}
                            onChange={(e) =>
                              setPkgSel((prev) => ({ ...prev, [m.id]: e.target.value }))
                            }
                          >
                            {pkgs.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}{p.is_default ? ' ★' : ''}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <span className="text-muted small">
                            {selectedPkg?.unit_label ?? selectedPkg?.name ?? m.unit}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {filledCount === 0 && (
            <p className="text-muted small mt-2 mb-0">
              Nhập số lượng vào các ô trên. Dòng để trống sẽ không được lưu.
            </p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>Hủy</Button>
          <Button variant="primary" type="submit" disabled={loading || filledCount === 0}>
            {loading ? 'Đang nhập...' : `Nhập kho${filledCount > 0 ? ` (${filledCount} NVL)` : ''}`}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
